<template>
  <ul ref="shipmentStopList" role="list" class="proposed-shipment-stop-list">
    <li
      v-for="stop of internalShipment.stops"
      :key="stop.key"
      :data-stop-sequence-number="stop.sequenceNumber"
      class="draggable-location-card"
    >
      <ArrangeableLocationCard
        :id="`${id}-stop-${stop.sequenceNumber}`"
        :line-items="lineItemsByStop[stop.sequenceNumber!]"
        :stop="stop"
        :resequence-error="
          resequenceStateByStop[stop.sequenceNumber!]?.canResequenceReason
        "
        :require-both-dates="requireBothDates"
        class="drag-item"
      />
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { useSortable } from '@vueuse/integrations/useSortable';
import isEqual from 'lodash/isEqual';
import { SortableEvent } from 'sortablejs';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, useTemplateRef, watch } from 'vue';

import { Shipment } from '@/model';
import { ConsolidatedShipment, ResequenceState } from '@/model/consolidation';
import { ProposedShipment } from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

import ArrangeableLocationCard from '@/components/location/ArrangeableLocationCard.vue';

export type ArrangeableShipment =
  | ConsolidatedShipment
  | Shipment
  | ProposedShipment
  | RoutePlanProposedShipment;
export type ArrangeableShipmentStop = ArrangeableShipment['stops'][number];
export type ArrangeableShipmentLineItem =
  ArrangeableShipment['lineItems'][number];

export type LineItemsBySequenceNumberMap = Record<
  number,
  ArrangeableShipmentLineItem[]
>;

const DEFAULT_RESEQUENCE_STATE: ResequenceState = {
  canResequence: true,
  canResequenceReason: '',
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Object as PropType<ArrangeableShipment>,
    required: true,
  },
});
const { id, shipment } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:shipment', shipment: ArrangeableShipment): void;
}>();

const shipmentStopList = useTemplateRef('shipmentStopList');

const resequenceStateByStop: Ref<Record<number, ResequenceState>> = ref({});

const internalShipment = computed({
  get() {
    return shipment.value;
  },
  set(updatedShipment: ArrangeableShipment) {
    if (!isEqual(updatedShipment, shipment.value)) {
      emit('update:shipment', updatedShipment);
    }
  },
});

const requireBothDates = computed(() => {
  return (
    shipment.value instanceof Shipment &&
    shipment.value.requireBothArrivalAndDepartureOnStops
  );
});

const lineItemsByStop: Ref<LineItemsBySequenceNumberMap> = computed(() => {
  return internalShipment.value.stops.reduce(
    (accumulator, stop: ArrangeableShipmentStop) => {
      const sequenceNumber = stop.sequenceNumber ?? -1;
      const lineItems = internalShipment.value.lineItems;
      const lineItemsByStop = lineItems.filter(
        (li) =>
          li.pickupStopSequenceNumber === sequenceNumber ||
          li.dropOffStopSequenceNumber === sequenceNumber,
      );
      accumulator[sequenceNumber] = lineItemsByStop;
      return accumulator;
    },
    {} as LineItemsBySequenceNumberMap,
  );
});

useSortable(shipmentStopList, [], {
  animation: 250,
  filter: '.not-draggable',
  onChange(e: SortableEvent) {
    if (e.oldIndex === undefined || e.newIndex === undefined) {
      console.warn(`Missing 'oldIndex' and/or 'newIndex'`, e);
      return;
    }

    const oldSequenceNumber = e.oldIndex + 1;
    const newSequenceNumber = e.newIndex + 1;
    resequenceStateByStop.value[oldSequenceNumber] =
      internalShipment.value.validateStopResequencing(
        oldSequenceNumber,
        newSequenceNumber,
      );
  },
  onUpdate(e: SortableEvent) {
    if (e.oldIndex === undefined || e.newIndex === undefined) {
      console.warn(`Missing 'oldIndex' and/or 'newIndex'`, e);
      return;
    }

    const { oldIndex, newIndex } = e;
    const oldSeqNum = oldIndex + 1;
    const newSeqNum = newIndex + 1;

    const canResequence = resequenceStateByStop.value[oldSeqNum].canResequence;

    if (canResequence) {
      internalShipment.value.resequenceStop(oldSeqNum, newSeqNum);
    } else {
      // sortablejs **will** manually move the DOM elements at this point
      // I have not found a way to prevent that
      // It is DOM manipulation, so vue is blind to it
      // So to get the dom "correct", we have to force vue to rerender the list
      // Change a mutable field, then put it back asap
      // This is gross, please improve
      const stop = internalShipment.value.stops[oldIndex];
      stop.sequenceNumber = Number.MAX_SAFE_INTEGER;
      nextTick(() => (stop.sequenceNumber = oldSeqNum));
    }
  },
  onEnd() {
    resequenceStateByStop.value = internalShipment.value.stops.reduce(
      (agg, curr: ArrangeableShipmentStop) => {
        agg[curr.sequenceNumber!] = {
          ...DEFAULT_RESEQUENCE_STATE,
          s: `${curr.location.locationName} ${curr.location.city}`,
        };
        return agg;
      },
      {} as Record<number, ResequenceState>,
    );
  },
});

watch(
  () => internalShipment.value.stops,
  (newVal) => {
    resequenceStateByStop.value = newVal.reduce(
      (agg, curr: ArrangeableShipmentStop) => {
        agg[curr.sequenceNumber!] = {
          ...DEFAULT_RESEQUENCE_STATE,
          s: `${curr.location.locationName} ${curr.location.city}`,
        };
        return agg;
      },
      {} as Record<number, ResequenceState>,
    );
  },
  { deep: true, immediate: true },
);
</script>

<style>
.proposed-shipment-stop-list {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);
  list-style: none;
  margin: 0;
  padding: 0;

  .draggable-location-card {
    display: flex;
    align-items: center;
    gap: var(--gap-1);
  }

  .sortable-ghost {
    --arrangeable-location-card-border-color: var(--success-color);
  }
}
</style>
